import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { InvitationType } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen satır sayısı — son satırda "aşağı" pasif olması için. */
  rowCount: number
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: InvitationType, dir: 'up' | 'down') => void
  onEdit: (row: InvitationType) => void
  onToggle: (row: InvitationType, active: boolean) => void
  onDelete: (row: InvitationType) => void
}

/**
 * Davet türü tablosunun sütunları (paylaşılan ui/DataTable'a beslenir). Baştaki **sıra** kolonu yan yana
 * ↑/↓ okları (ortak SortSpinner) — sıra numarası gösterilmez, taşıma anında kalıcı. Durum satır-içi
 * InputSwitch (Aktif/Pasif) + satır aksiyonları (düzenle/sil). **Korumalı sistem kaydı yoktur.**
 * (023 EventTypeColumns deseniyle aynı.)
 */
export function invitationTypeColumns({
  t,
  localized,
  busyId,
  rowCount,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={t('activity.invitationTypes.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: InvitationType, { rowIndex }) => (
        <SortSpinner
          onUp={() => onMove(row, 'up')}
          onDown={() => onMove(row, 'down')}
          disableUp={rowIndex === 0}
          disableDown={rowIndex === rowCount - 1}
          busy={reordering}
          labelUp={t('common.reorder.up')}
          labelDown={t('common.reorder.down')}
        />
      )}
    />,
    <Column
      key="name"
      header={t('activity.invitationTypes.columns.name')}
      body={(row: InvitationType) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('activity.invitationTypes.columns.status')}
      style={{ width: '8rem' }}
      body={(row: InvitationType) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('activity.invitationTypes.status.active')
              : t('activity.invitationTypes.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('activity.invitationTypes.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: InvitationType) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('activity.invitationTypes.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('activity.invitationTypes.actions.edit')}
              tooltip={t('activity.invitationTypes.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('activity.invitationTypes.actions.delete')}
              tooltip={t('activity.invitationTypes.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
